import {
  FROZEN_SAVE_SHOPPING_LIST_PAYLOAD_VERSION,
  parseFrozenSaveShoppingListPayload,
} from '@dohm/handoff';
import type {
  ArchibaldHandoffPort,
  ArchibaldHandoffPreparation,
  ArchibaldHandoffStatus,
  ArchibaldKeyExchangeResult,
  ArchibaldReceiptRetrieval,
  ArchibaldReceiptVerification,
  HandoffTarget,
} from '../application/contracts';
import type { RecipeShoppingList } from '../domain/model';

const API_ORIGIN = import.meta.env.VITE_DOHM_HANDOFF_API_ORIGIN || window.location.origin;
const API_PREFIX = '/api/handoff';
const DEVICE_STORAGE_KEY = 'frozen-save.handoff-device-id';
const TRANSFER_STORAGE_KEY = 'frozen-save.handoff-transfer-v1';
const textEncoder = new TextEncoder();

interface PendingIntent {
  readonly contentKey: Uint8Array;
  readonly expiresAt: string;
  readonly intentId: string;
  readonly listId: string;
  readonly target: HandoffTarget;
  wrapped: boolean;
}

interface PersistedTransfer {
  readonly expiresAt: string;
  readonly intentId: string;
  readonly listId: string;
  readonly target: HandoffTarget;
  readonly v: 1 | 2;
}

interface ApiErrorBody {
  readonly code?: unknown;
}

export class ArchibaldHandoffApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

const base64Url = (value: Uint8Array): string => {
  let binary = '';
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readPersistedTransfer = (listId: string): PersistedTransfer | null => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(TRANSFER_STORAGE_KEY) ?? 'null');
    if (
      !isRecord(parsed) || (parsed.v !== 1 && parsed.v !== 2) || parsed.listId !== listId ||
      typeof parsed.intentId !== 'string' || !/^hnd_[A-Za-z0-9_-]{16,124}$/u.test(parsed.intentId) ||
      typeof parsed.expiresAt !== 'string' || !Number.isFinite(Date.parse(parsed.expiresAt)) ||
      (parsed.v === 2 && parsed.target !== 'archibald' && parsed.target !== 'paniprix')
    ) return null;
    return {
      v: parsed.v,
      listId,
      intentId: parsed.intentId,
      expiresAt: parsed.expiresAt,
      target: parsed.v === 2 ? parsed.target as HandoffTarget : 'archibald',
    };
  } catch {
    return null;
  }
};

const persistTransfer = (pending: PendingIntent) => {
  const value: PersistedTransfer = {
    v: 2,
    listId: pending.listId,
    intentId: pending.intentId,
    expiresAt: pending.expiresAt,
    target: pending.target,
  };
  localStorage.setItem(TRANSFER_STORAGE_KEY, JSON.stringify(value));
};

const isSafeBaseUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.username === '' && url.password === '' &&
      url.search === '' && url.hash === '';
  } catch {
    return false;
  }
};

const isStatus = (value: unknown): value is ArchibaldHandoffStatus => {
  if (!isRecord(value)) return false;
  if (value.state === 'disconnected') return Object.keys(value).length === 1;
  if (value.state === 'identity_pending') {
    return typeof value.expiresAt === 'string' && Number.isFinite(Date.parse(value.expiresAt));
  }
  if (value.state === 'ready') {
    return (value.access === 'entitled' || value.access === 'trial_active') &&
      isSafeBaseUrl(value.baseUrl);
  }
  return value.state === 'fallback' &&
    (value.access === 'trial_eligible' || value.access === 'trial_expired' ||
      value.access === 'declined' || value.access === 'unavailable') &&
    (value.fallbackUrl === null || isSafeBaseUrl(value.fallbackUrl));
};

const request = async <T>(
  path: string,
  init: { readonly body?: unknown; readonly method?: 'DELETE' | 'GET' | 'POST' } = {},
): Promise<T> => {
  const method = init.method ?? 'POST';
  const requestInit: RequestInit = {
    cache: 'no-store',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Frozen-Save-Request': 'handoff-v1',
      ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    method,
    redirect: 'error',
  };
  if (init.body !== undefined) requestInit.body = JSON.stringify(init.body);
  const response = await fetch(new URL(`${API_PREFIX}${path}`, API_ORIGIN), requestInit);
  const body = await response.json().catch(() => ({ code: 'HANDOFF_RESPONSE_INVALID' })) as ApiErrorBody;
  if (!response.ok) {
    throw new ArchibaldHandoffApiError(
      typeof body.code === 'string' ? body.code : 'HANDOFF_REQUEST_FAILED',
      response.status,
    );
  }
  return body as T;
};

const deviceId = (): string => {
  const existing = localStorage.getItem(DEVICE_STORAGE_KEY);
  if (existing != null && /^fs-web-[0-9a-f-]{36}$/u.test(existing)) return existing;
  const created = `fs-web-${crypto.randomUUID()}`;
  localStorage.setItem(DEVICE_STORAGE_KEY, created);
  return created;
};

const publicPayload = (list: RecipeShoppingList) => parseFrozenSaveShoppingListPayload({
  contractVersion: FROZEN_SAVE_SHOPPING_LIST_PAYLOAD_VERSION,
  recipeTitle: list.recipeTitle,
  locale: list.locale,
  items: list.items.map((item) => ({
    id: item.id,
    label: item.label,
    checked: item.checked,
    requiredQuantity: item.requiredQuantity,
    unit: item.unit,
  })),
});

const encryptPayload = async (list: RecipeShoppingList) => {
  const contentKey = crypto.getRandomValues(new Uint8Array(32));
  const key = await crypto.subtle.importKey('raw', contentKey, { name: 'AES-GCM' }, false, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = textEncoder.encode(JSON.stringify(publicPayload(list)));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext));
  plaintext.fill(0);
  return {
    contentKey,
    encryptedEnvelope: {
      alg: 'A256GCM',
      ciphertext: base64Url(ciphertext),
      iv: base64Url(iv),
      keyRef: `frozen-save:${crypto.randomUUID()}`,
    },
  };
};

const wrapContentKey = async (
  contentKey: Uint8Array,
  keyDerivationContext: string,
  publicJwk: JsonWebKey,
) => {
  const recipientPublicKey = await crypto.subtle.importKey(
    'jwk',
    publicJwk,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
  const ephemeral = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveBits'],
  );
  const sharedSecret = new Uint8Array(await crypto.subtle.deriveBits(
    { name: 'ECDH', public: recipientPublicKey },
    ephemeral.privateKey,
    256,
  ));
  const material = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
  const wrappingKey = await crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32),
      info: textEncoder.encode(keyDerivationContext),
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt'],
  );
  sharedSecret.fill(0);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const rawContentKey = new Uint8Array(new ArrayBuffer(contentKey.byteLength));
  rawContentKey.set(contentKey);
  const wrapped = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    wrappingKey,
    rawContentKey.buffer,
  ));
  rawContentKey.fill(0);
  const exported = await crypto.subtle.exportKey('jwk', ephemeral.publicKey);
  if (
    exported.kty !== 'EC' || exported.crv !== 'P-256' ||
    typeof exported.x !== 'string' || typeof exported.y !== 'string'
  ) {
    throw new Error('HANDOFF_EPHEMERAL_KEY_INVALID');
  }
  return {
    alg: 'ECDH-ES+A256GCM',
    ciphertext: base64Url(wrapped),
    ephemeralPublicJwk: {
      crv: 'P-256',
      kty: 'EC',
      x: exported.x,
      y: exported.y,
    },
    iv: base64Url(iv),
  };
};

const parseReceiptInput = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (trimmed.length < 2 || trimmed.length > 4_096) throw new Error('HANDOFF_RECEIPT_INVALID');
  return JSON.parse(trimmed) as unknown;
};

const takeIdentityToken = (): string | null => {
  const url = new URL(window.location.href);
  const queryToken = url.searchParams.get('dohm_identity_token');
  const hash = new URLSearchParams(url.hash.replace(/^#/u, ''));
  const hashToken = hash.get('dohm_identity_token');
  const token = queryToken ?? hashToken;
  if (queryToken != null) url.searchParams.delete('dohm_identity_token');
  if (hashToken != null) {
    hash.delete('dohm_identity_token');
    url.hash = hash.toString();
  }
  if (token != null) window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  return token != null && /^[A-Za-z0-9._~-]{32,4096}$/u.test(token) ? token : null;
};

export class ProductionArchibaldHandoff implements ArchibaldHandoffPort {
  readonly available = true;
  private pending: PendingIntent | null = null;
  private identityToken: string | null = takeIdentityToken();

  async status(target: HandoffTarget): Promise<ArchibaldHandoffStatus> {
    try {
      if (this.identityToken != null) {
        const token = this.identityToken;
        this.identityToken = null;
        const consumed = await request<unknown>('/session/consume', { body: { target, token } });
        if (!isRecord(consumed) || !isStatus(consumed.status)) {
          throw new Error('HANDOFF_SESSION_RESPONSE_INVALID');
        }
        return consumed.status;
      }
      const result = await request<unknown>(`/resolve?target=${target}`);
      return isStatus(result) ? result : {
        state: 'fallback', access: 'unavailable', fallbackUrl: null,
      };
    } catch (error) {
      if (error instanceof ArchibaldHandoffApiError && error.status === 401) {
        return { state: 'disconnected' };
      }
      return { state: 'fallback', access: 'unavailable', fallbackUrl: null };
    }
  }

  async connect(email: string): Promise<ArchibaldHandoffStatus> {
    const result = await request<unknown>('/session/request', {
      body: { deviceId: deviceId(), email },
    });
    if (!isRecord(result) || !isStatus(result.status)) {
      throw new Error('HANDOFF_SESSION_RESPONSE_INVALID');
    }
    return result.status;
  }

  async disconnect(): Promise<void> {
    this.clearPending();
    await request('/session', { method: 'DELETE' });
  }

  async prepare(input: {
    readonly list: RecipeShoppingList;
    readonly confirmed: true;
    readonly target: HandoffTarget;
  }): Promise<ArchibaldHandoffPreparation> {
    if (input.confirmed !== true) {
      return { state: 'fallback', access: 'declined', fallbackUrl: null };
    }
    const status = await this.status(input.target);
    if (status.state === 'disconnected' || status.state === 'identity_pending') {
      return { state: 'fallback', access: 'unavailable', fallbackUrl: null };
    }
    if (status.state === 'fallback') return status;

    this.clearPending();
    const encrypted = await encryptPayload(input.list);
    try {
      const issued = await request<unknown>('/intent', {
        body: {
          encryptedEnvelope: encrypted.encryptedEnvelope,
          requestId: `fsIssue_${crypto.randomUUID()}`,
          target: input.target,
        },
      });
      if (
        !isRecord(issued) || issued.contractVersion !== 'dohm.handoff-gateway.v1' ||
        typeof issued.intentId !== 'string' || !/^hnd_[A-Za-z0-9_-]{16,124}$/u.test(issued.intentId) ||
        typeof issued.expiresAt !== 'string' || !Number.isFinite(Date.parse(issued.expiresAt)) ||
        typeof issued.token !== 'string' || !/^[A-Za-z0-9_-]{32,4096}$/u.test(issued.token)
      ) {
        throw new Error('HANDOFF_ISSUE_RESPONSE_INVALID');
      }
      this.pending = {
        contentKey: encrypted.contentKey,
        expiresAt: issued.expiresAt,
        intentId: issued.intentId,
        listId: input.list.id,
        target: input.target,
        wrapped: false,
      };
      return {
        state: 'ready',
        access: status.access,
        baseUrl: status.baseUrl,
        token: issued.token,
        intentId: issued.intentId,
        expiresAt: issued.expiresAt,
      };
    } catch (error) {
      encrypted.contentKey.fill(0);
      throw error;
    }
  }

  async completeKeyExchange(): Promise<ArchibaldKeyExchangeResult> {
    const pending = this.pending;
    if (pending == null) return { state: 'lost' };
    if (Date.parse(pending.expiresAt) <= Date.now()) {
      this.clearPending(true);
      return { state: 'expired' };
    }
    if (pending.wrapped) return { state: 'wrapped', intentId: pending.intentId };
    try {
      const exchange = await request<unknown>('/key-exchange', {
        body: { intentId: pending.intentId },
      });
      if (!isRecord(exchange) || exchange.contractVersion !== 'dohm.handoff-key-exchange.v1') {
        return { state: 'unavailable' };
      }
      if (exchange.status === 'wrapped') {
        pending.wrapped = true;
        pending.contentKey.fill(0);
        persistTransfer(pending);
        return { state: 'wrapped', intentId: pending.intentId };
      }
      const recipient = exchange.recipient;
      if (
        exchange.status !== 'awaiting-source-wrap' ||
        typeof exchange.keyDerivationContext !== 'string' ||
        exchange.keyDerivationContext.length > 512 ||
        !isRecord(recipient) || typeof recipient.keyId !== 'string' ||
        !isRecord(recipient.publicJwk)
      ) {
        return { state: 'unavailable' };
      }
      const keyEnvelope = await wrapContentKey(
        pending.contentKey,
        exchange.keyDerivationContext,
        recipient.publicJwk,
      );
      const stored = await request<unknown>('/key-envelope', {
        body: {
          intentId: pending.intentId,
          keyEnvelope,
          recipientKeyId: recipient.keyId,
          requestId: `fsWrap_${crypto.randomUUID()}`,
        },
      });
      if (!isRecord(stored) || stored.status !== 'wrapped') return { state: 'unavailable' };
      pending.wrapped = true;
      pending.contentKey.fill(0);
      persistTransfer(pending);
      return { state: 'wrapped', intentId: pending.intentId };
    } catch (error) {
      if (
        error instanceof ArchibaldHandoffApiError && error.status === 409 &&
        (error.code === 'HANDOFF_TARGET_DEVICE_NOT_CLAIMED' ||
          error.code === 'HANDOFF_TARGET_DEVICE_UNAVAILABLE')
      ) {
        return { state: 'awaiting_target' };
      }
      return { state: 'unavailable' };
    }
  }

  async retrieveReceipt(listId: string): Promise<ArchibaldReceiptRetrieval> {
    const inMemory = this.pending?.wrapped === true && this.pending.listId === listId
      ? this.pending
      : null;
    const transfer = inMemory ?? readPersistedTransfer(listId);
    if (transfer == null) return { state: 'none', intentId: null };
    if (Date.parse(transfer.expiresAt) <= Date.now()) {
      this.clearPending(true);
      return { state: 'expired', intentId: transfer.intentId };
    }
    try {
      const result = await request<unknown>('/receipt', {
        body: { intentId: transfer.intentId },
      });
      if (
        !isRecord(result) || result.intentId !== transfer.intentId ||
        typeof result.state !== 'string'
      ) {
        return { state: 'invalid', intentId: transfer.intentId };
      }
      if (
        result.state === 'pending' && result.expiresAt === transfer.expiresAt &&
        typeof result.retryAfterSeconds === 'number' &&
        Number.isInteger(result.retryAfterSeconds) &&
        result.retryAfterSeconds >= 1 && result.retryAfterSeconds <= 30
      ) {
        return {
          state: 'pending',
          intentId: transfer.intentId,
          expiresAt: transfer.expiresAt,
          retryAfterSeconds: result.retryAfterSeconds,
        };
      }
      if (
        result.state === 'accepted' && result.signatureVerified === true &&
        result.sourceMayRelease === true
      ) {
        this.clearPending();
        return {
          state: 'accepted',
          intentId: transfer.intentId,
          target: transfer.target,
          verifiedAt: new Date().toISOString(),
        };
      }
      if (result.state === 'declined' || result.state === 'expired') {
        this.clearPending();
        return { state: result.state, intentId: transfer.intentId };
      }
      return { state: 'invalid', intentId: transfer.intentId };
    } catch (error) {
      if (error instanceof ArchibaldHandoffApiError && error.status === 410) {
        this.clearPending(true);
        return { state: 'expired', intentId: transfer.intentId };
      }
      return { state: 'unavailable', intentId: transfer.intentId };
    }
  }

  async verifyReceipt(
    receiptValue: unknown,
    target: HandoffTarget,
  ): Promise<ArchibaldReceiptVerification> {
    try {
      const receipt = parseReceiptInput(receiptValue);
      const verified = await request<unknown>('/receipt/verify', { body: { receipt } });
      if (
        !isRecord(verified) || typeof verified.intentId !== 'string' ||
        verified.target !== target || typeof verified.status !== 'string'
      ) {
        return { state: 'invalid', intentId: null };
      }
      if (
        verified.status === 'accepted' && verified.signatureVerified === true &&
        verified.sourceMayRelease === true
      ) {
        this.clearPending();
        return {
          state: 'accepted',
          intentId: verified.intentId,
          target,
          verifiedAt: new Date().toISOString(),
        };
      }
      if (verified.status === 'declined' || verified.status === 'expired') {
        this.clearPending();
        return { state: verified.status, intentId: verified.intentId };
      }
      return { state: 'invalid', intentId: verified.intentId };
    } catch {
      return { state: 'invalid', intentId: null };
    }
  }

  async exportReceipt(listId: string, known?: { intentId: string; target: HandoffTarget }): Promise<string> {
    const transfer = known ?? (this.pending?.listId === listId ? this.pending : readPersistedTransfer(listId));
    if (transfer == null) throw new Error('HANDOFF_RECEIPT_REFERENCE_REQUIRED');
    const value = await request<unknown>('/receipt/export', {
      body: { intentId: transfer.intentId, target: transfer.target },
    });
    if (!isRecord(value) || value.contractVersion !== 'dohm.handoff-private-receipt.v1' ||
      value.intentId !== transfer.intentId || value.target !== transfer.target ||
      !['accepted', 'declined', 'expired'].includes(String(value.status)) ||
      typeof value.keyId !== 'string' || !value.keyId ||
      typeof value.occurredAt !== 'string' || !Number.isFinite(Date.parse(value.occurredAt)) ||
      typeof value.signature !== 'string' || !value.signature
    ) throw new Error('HANDOFF_RECEIPT_INVALID');
    return JSON.stringify({ contractVersion: value.contractVersion, intentId: value.intentId,
      target: value.target, status: value.status, keyId: value.keyId,
      occurredAt: value.occurredAt, signature: value.signature });
  }

  private clearPending(keepReference = false): void {
    this.pending?.contentKey.fill(0);
    this.pending = null;
    if (!keepReference) localStorage.removeItem(TRANSFER_STORAGE_KEY);
  }
}

/** Alias conservé pour les tests et les migrations de source 1.5.0. */
export const SameOriginArchibaldHandoff = ProductionArchibaldHandoff;
